import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';

import { ListingStatus, ListingStrategy } from '@invenco/sales-interface/interfaces';

import { Badge } from '@/components/core/badge';
import { DialogBody, DialogDescription, DialogHeader, DialogTitle } from '@/components/core/dialog';
import { SelectItem } from '@/components/core/select';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/core/table';
import { FormDialogContent } from '@/components/forms/form-dialog';
import { SelectFormField } from '@/components/forms/select-form-field';
import { StatusBadge } from '@/components/status-badge';
import { cn, formatNumber } from '@/shared/helpers';
import { listingStatusType } from '@/shared/status-maps';
import { listingStatusTitle, listingStrategyTitle } from '@/shared/title-maps';

import { ListingListItem } from './listing-list-mapper';

const formSchema = z.object({
  status: z.enum(ListingStatus),
  strategy: z.enum(ListingStrategy),
});
type FormValues = z.infer<typeof formSchema>;

type Props = {
  selectedListings: ListingListItem[];
  onSubmit: (data: FormValues) => Promise<void>;
};

function getStatusDescription(status: ListingStatus): string {
  switch (status) {
    case ListingStatus.DRAFT:
      return 'A draft listing will not sync the listed quantity to the sales channel. Use this status to have the sync ignore the listing while you are preparing or editing it.';
    case ListingStatus.ACTIVE:
      return "An active listing will sync the listed quantity to the sales channel. Make a listing active when you're ready to go live and include it in the sync.";
    case ListingStatus.ARCHIVED:
      return 'An archived listing will not sync the listed quantity to the sales channel and is considered inactive. Archived listings only appear under the Archived listings tab.';
    default:
      return '';
  }
}

function getStrategyDescription(strategy: ListingStrategy): string {
  switch (strategy) {
    case ListingStrategy.DYNAMIC_AVAILABLE:
      return 'A dynamic listing strategy will list the available inventory from all of the fulfillment locations linked to this channel. Use this strategy to make whatever inventory you have available for sale in this channel.';
    case ListingStrategy.PRESELL:
      return 'A presell listing strategy will add the expected inventory from open ASNs and the available inventory from the fulfillment locations. Use this strategy to presell incoming stock available for sale in this channel even before it arrives.';
    case ListingStrategy.SOLD_OUT:
      return 'A sold out listing strategy will list a fixed value of 0 in this channel. Use this strategy to always show 0 available stock for sale in this sales channel, even if you have available or expected inventory.';
    default:
      return '';
  }
}

function getListedQuantity(listing: ListingListItem, strategy: ListingStrategy): number {
  switch (strategy) {
    case ListingStrategy.SOLD_OUT:
      return 0;
    case ListingStrategy.DYNAMIC_AVAILABLE:
      return listing.qtyAvailable;
    case ListingStrategy.PRESELL:
      return listing.qtyExpected + listing.qtyAvailable;
    default:
      return 0;
  }
}

const MAX_PREVIEW_ROWS = 5;

function PreviewTable({
  listings,
  strategy,
}: {
  listings: ListingListItem[];
  strategy: ListingStrategy;
}) {
  const displayedListings = listings.slice(0, MAX_PREVIEW_ROWS);
  const hasMore = listings.length > MAX_PREVIEW_ROWS;

  const showExpected = strategy === ListingStrategy.PRESELL;
  const showAvailable =
    strategy === ListingStrategy.DYNAMIC_AVAILABLE || strategy === ListingStrategy.PRESELL;

  return (
    <Table>
      <TableCaption className="mt-2">Strategy preview</TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          {showExpected && <TableHead className="text-right">Expected</TableHead>}
          {showAvailable && <TableHead className="text-right">Available</TableHead>}
          <TableHead className="text-right">Listed</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {displayedListings.map((listing) => {
          const newListedQty = getListedQuantity(listing, strategy);
          return (
            <TableRow key={listing.id}>
              <TableCell>
                <Badge variant="secondary">
                  <span className="max-w-30 truncate">{listing.name}</span>
                </Badge>
              </TableCell>
              {showExpected && (
                <TableCell className="text-right">{formatNumber(listing.qtyExpected)}</TableCell>
              )}
              {showAvailable && (
                <TableCell className="text-right">{formatNumber(listing.qtyAvailable)}</TableCell>
              )}
              <TableCell
                className={cn('text-right', newListedQty !== listing.qtyListed && 'italic')}
              >
                {formatNumber(newListedQty)}
              </TableCell>
            </TableRow>
          );
        })}
        {hasMore && (
          <TableRow>
            <TableCell
              colSpan={2 + (showExpected ? 1 : 0) + (showAvailable ? 1 : 0)}
              className="text-center text-muted-foreground"
            >
              ...
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

export function EditListingsDialogContent({ selectedListings, onSubmit }: Props) {
  const initialValues = useMemo(() => {
    const firstListing = selectedListings[0];
    const allSameStatus = selectedListings.every((l) => l.status === firstListing?.status);
    const allSameStrategy = selectedListings.every(
      (l) => l.listingStrategy === firstListing?.listingStrategy,
    );

    return {
      status: allSameStatus ? firstListing?.status : ListingStatus.ACTIVE,
      strategy: allSameStrategy ? firstListing?.listingStrategy : ListingStrategy.DYNAMIC_AVAILABLE,
    };
  }, [selectedListings]);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      status: initialValues.status ?? ListingStatus.DRAFT,
      strategy: initialValues.strategy ?? ListingStrategy.DYNAMIC_AVAILABLE,
    },
  });

  const watchedStatus = form.watch('status');
  const watchedStrategy = form.watch('strategy');

  return (
    <FormDialogContent
      form={form}
      onSubmit={async (data) => {
        await onSubmit(data);
      }}
    >
      <DialogHeader>
        <DialogTitle>Edit Strategy ({selectedListings.length})</DialogTitle>
        <DialogDescription>
          Update the status and strategy for the selected listings
        </DialogDescription>
      </DialogHeader>
      <DialogBody>
        <SelectFormField
          control={form.control}
          name="status"
          label="Listing status"
          description={getStatusDescription(watchedStatus)}
          className="w-full"
        >
          {[ListingStatus.ACTIVE, ListingStatus.DRAFT, ListingStatus.ARCHIVED].map((status) => (
            <SelectItem key={status} value={status}>
              <StatusBadge status={status} title={listingStatusTitle} type={listingStatusType} />
            </SelectItem>
          ))}
        </SelectFormField>
        <SelectFormField
          control={form.control}
          name="strategy"
          label="Stock manipulation strategy"
          description={getStrategyDescription(watchedStrategy)}
          className="w-full"
        >
          {[
            ListingStrategy.DYNAMIC_AVAILABLE,
            ListingStrategy.PRESELL,
            ListingStrategy.SOLD_OUT,
          ].map((strategy) => (
            <SelectItem key={strategy} value={strategy}>
              {listingStrategyTitle[strategy]}
            </SelectItem>
          ))}
        </SelectFormField>

        <PreviewTable listings={selectedListings} strategy={watchedStrategy} />
      </DialogBody>
    </FormDialogContent>
  );
}
